import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import { Cancel, Info, KeyboardArrowDown } from '@mui/icons-material';
import { Autocomplete, IconButton, InputAdornment } from '@mui/material';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import classnames from 'classnames';
import HTMLReactParser from 'html-react-parser';
import { isEqual } from 'lodash';

import { useAutocomplete, withWidget } from '@ezbobdev/ui-commons';

import { css, styled } from '../../Utils/style';
import TextHighlights from '../../components/TextHighlights/TextHighlights';
import { BaseMaterialTextField, MaterialChip, MaterialTooltip } from '../../index';

const AutocompleteStyled = styled(Autocomplete)`
  ${({ theme }) => {
    if (theme.direction === 'rtl') {
      return css`
        &.hasTooltip {
          .MuiAutocomplete-clearIndicator {
            margin: 0 ${theme.spacing(4)} 0 0;
          }
        }

        .MuiFormControl-root {
          .MuiInputBase-root.MuiOutlinedInput-root {
            padding-right: ${theme.spacing(8)};
          }

          .MuiInputBase-root.MuiInput-root.MuiInputBase-fullWidth {
            padding: 0 ${theme.spacing(3)} 0 0;
            display: inline-flex;
            display: -webkit-inline-box;
            display: -ms-inline-flexbox;
            display: -moz-inline-box;
          }

          .MuiAutocomplete-endAdornment {
            .MuiAutocomplete-popupIndicator {
              margin-right: 0;
              padding: 2px 0 2px 2px;
            }
          }

          .MuiInputAdornment-root {
            &.MuiInputAdornment-positionStart {
              .icon {
                width: 2.4rem;
                height: 2.4rem;
              }
            }
          }
        }
      `;
    }

    return css`
      .MuiInputAdornment-root {
        &.MuiInputAdornment-positionStart {
          .icon {
            width: 2.4rem;
            height: 2.4rem;
          }
        }
      }
    `;
  }}
`;

const MaterialAutocomplete = (props) => {
  const {
    configByName,
    className,
    isServerSide,
    freeSolo,
    multiple,
    variant,
    options,
    value,
    noOptionsText,
    matchInsideWords,
    privacyRules,
    MUIAutocompleteProps,
    handleInputChange,
    handleChange,
    handleBlur,
    hasTooltip,
    isRequired,
    renderTags,
    t,
    clearText,
    openText,
    closeText,
    startAdornment,
    readOnly,
    disabled,
    inputValue,
    tooltipColor,
    isDropdownOpen,
    openDropdown,
    closeDropdown,
    autocompleteWrapperRef,
  } = useAutocomplete(props, MaterialAutocomplete.displayName);

  const handleDelayedBlur = (originalOnBlur) => (e) =>
    setTimeout(() => {
      originalOnBlur?.(e);
    }, 0);

  return useMemo(
    () => (
      <AutocompleteStyled
        ref={autocompleteWrapperRef}
        disablePortal
        open={isDropdownOpen}
        onOpen={openDropdown}
        onClose={closeDropdown}
        className={classnames(MaterialAutocomplete.displayName, configByName, className, hasTooltip && 'hasTooltip')}
        freeSolo={freeSolo}
        readOnly={readOnly}
        disabled={disabled}
        multiple={multiple}
        options={options}
        value={value}
        inputValue={inputValue}
        clearOnBlur={false}
        getOptionLabel={(option) => option?.label || option}
        filterOptions={isServerSide ? (x) => x : undefined}
        noOptionsText={noOptionsText}
        isOptionEqualToValue={(option, value) => {
          const optionToCompare = option?.value ?? option;
          const valueToCompare = value?.value ?? value;

          return isEqual(optionToCompare, valueToCompare);
        }}
        clearText={clearText}
        openText={openText}
        closeText={closeText}
        slotProps={{
          clearIndicator: {
            tabIndex: 0,
            title: undefined,
            sx: { visibility: 'visible' },
          },
          popupIndicator: {
            tabIndex: -1,
            title: undefined,
            className: classnames({ 'Mui-disabled': disabled || readOnly }),
          },
          listbox: {
            sx: {
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
              touchAction: 'pan-y',
              overflowY: 'auto',
            },
          },
          paper: {
            tabIndex: 0,
            sx: {
              WebkitOverflowScrolling: 'touch',
              touchAction: 'pan-y',
            },
          },
          popper: {
            modifiers: [
              {
                name: 'computeStyles',
                options: {
                  gpuAcceleration: false,
                },
              },
            ],
          },
        }}
        renderOption={(optionProps, currentOption, currentState) => {
          const { label, labelPrefix } = currentOption;
          const { selected, inputValue } = currentState;
          const matches = match(label, inputValue, {
            insideWords: matchInsideWords,
            findAllOccurrences: matchInsideWords,
          });
          const parts = parse(label, matches);

          return (
            <div {...optionProps} className={classnames(optionProps.className, 'Option', { selected })}>
              {labelPrefix && HTMLReactParser(labelPrefix)}
              <span>
                {parts.map((letter, index) => {
                  const { highlight, text } = letter;

                  return (
                    <TextHighlights
                      highlight={highlight}
                      className="OptionText"
                      key={index}
                      data-dd-privacy={privacyRules.option}
                      data-dd-action-name={privacyRules.option === 'mask' ? 'option' : text?.toString()}
                    >
                      {HTMLReactParser(text?.toString())}
                    </TextHighlights>
                  );
                })}
              </span>
            </div>
          );
        }}
        popupIcon={<KeyboardArrowDown />}
        renderInput={(props) => (
          <BaseMaterialTextField
            {...props}
            isRequired={isRequired}
            FormHelperTextProps={{ 'aria-hidden': isDropdownOpen }}
            InputProps={{
              ...props.InputProps,
              className: classnames({ 'Mui-disabled': disabled || readOnly }),
              inputComponent: undefined,
              startAdornment: startAdornment ? (
                <InputAdornment position="start">{startAdornment}</InputAdornment>
              ) : (
                props.InputProps.startAdornment
              ),
              endAdornment: (
                <>
                  {props.InputProps.endAdornment}
                  {hasTooltip && (
                    <InputAdornment position="end">
                      <MaterialTooltip name={`${configByName}.adornment`}>
                        <IconButton
                          onClick={(e) => e.stopPropagation()}
                          aria-label={t(`globals.tooltipTriggerLabel`)}
                          aria-describedby={`${configByName}-tooltip-content`}
                        >
                          <Info alt={t(`globals.infoIconAlt`)} color={tooltipColor} />
                        </IconButton>
                      </MaterialTooltip>
                    </InputAdornment>
                  )}
                </>
              ),
            }}
            inputProps={{
              ...props.inputProps,
              ...(readOnly && { role: undefined }),
              onBlur: handleDelayedBlur(props.inputProps.onBlur),
            }}
            name={configByName}
            variant={variant}
            fullWidth
          />
        )}
        renderTags={
          renderTags
            ? (values, getTagProps) =>
                values.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });

                  return (
                    <MaterialChip
                      key={key}
                      label={option.label}
                      {...tagProps}
                      role={undefined}
                      deleteIcon={
                        <IconButton
                          aria-label={t('globals.removeOption', { option: option.label })}
                          size="small"
                          disableRipple
                        >
                          <Cancel fontSize="small" />
                        </IconButton>
                      }
                    />
                  );
                })
            : (value) => value.map((x) => x.label).join(', ')
        }
        onInputChange={handleInputChange}
        onChange={handleChange}
        onBlur={handleBlur}
        {...MUIAutocompleteProps}
      />
    ),
    [
      configByName,
      className,
      freeSolo,
      multiple,
      variant,
      value,
      noOptionsText,
      matchInsideWords,
      privacyRules,
      MUIAutocompleteProps,
      handleInputChange,
      handleChange,
      handleBlur,
      inputValue,
      hasTooltip,
      t,
      startAdornment,
      disabled,
      readOnly,
      isRequired,
      renderTags,
      isDropdownOpen,
    ],
  );
};

MaterialAutocomplete.displayName = 'MaterialAutocomplete';

MaterialAutocomplete.propTypes = {
  name: PropTypes.string.isRequired,
  className: PropTypes.string,
  variant: PropTypes.oneOf(['outlined', 'standard', 'filled']),
  textFieldProps: PropTypes.object,
  MUIAutocompleteProps: PropTypes.object,
  multiple: PropTypes.bool,
  values: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  ),
};

export default React.memo(withWidget(MaterialAutocomplete, { includeCaseMapProps: ['name'] }));
